import type { Coordinates, SpotId } from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const MAX_RESULTS = 5;

export interface PlaceSearchResult {
  id: string;
  name: string;
  displayName: string;
  category: string;
  type: string;
  coordinates: Coordinates;
}

export interface PlaceSearchProvider {
  name: string;
  attribution: string;
  attributionUrl: string;
}

export interface PlaceSearchResponse {
  results: PlaceSearchResult[];
  provider: PlaceSearchProvider;
}

interface PlaceSearchOptions {
  signal?: AbortSignal;
  spot?: SpotId;
  apiBase?: string;
  fetcher?: typeof fetch;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isFiniteCoordinate = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const readResult = (value: unknown): PlaceSearchResult => {
  if (!isRecord(value) || !isRecord(value.coordinates)) {
    throw new Error("invalid_geocode_result");
  }
  const { latitude, longitude } = value.coordinates;
  if (
    typeof value.id !== "string" ||
    value.id.length === 0 ||
    value.id.length > 160 ||
    typeof value.name !== "string" ||
    value.name.length === 0 ||
    value.name.length > 240 ||
    typeof value.displayName !== "string" ||
    value.displayName.length === 0 ||
    value.displayName.length > 600 ||
    typeof value.category !== "string" ||
    value.category.length > 80 ||
    typeof value.type !== "string" ||
    value.type.length > 80 ||
    !isFiniteCoordinate(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !isFiniteCoordinate(longitude) ||
    longitude < -180 ||
    longitude > 180
  ) {
    throw new Error("invalid_geocode_result");
  }
  return {
    id: value.id,
    name: value.name,
    displayName: value.displayName,
    category: value.category,
    type: value.type,
    coordinates: { latitude, longitude },
  };
};

const readProvider = (value: unknown): PlaceSearchProvider => {
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    value.name.length === 0 ||
    value.name.length > 120 ||
    typeof value.attribution !== "string" ||
    value.attribution.length === 0 ||
    value.attribution.length > 240 ||
    typeof value.attributionUrl !== "string"
  ) {
    throw new Error("invalid_geocode_provider");
  }
  const attributionUrl = new URL(value.attributionUrl);
  if (attributionUrl.protocol !== "https:") {
    throw new Error("invalid_geocode_provider");
  }
  return {
    name: value.name,
    attribution: value.attribution,
    attributionUrl: attributionUrl.toString(),
  };
};

export const normalizePlaceQuery = (query: string): string =>
  query.normalize("NFKC").trim().replace(/\s+/g, " ");

export async function searchPlaces(
  rawQuery: string,
  {
    signal,
    spot,
    apiBase = API_BASE,
    fetcher = fetch,
  }: PlaceSearchOptions = {},
): Promise<PlaceSearchResponse> {
  const query = normalizePlaceQuery(rawQuery);
  if (query.length < 2 || query.length > 120) {
    throw new Error("invalid_geocode_query");
  }

  const response = await fetcher(`${apiBase}/api/v1/geocode`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({ query, ...(spot ? { spot } : {}) }),
    cache: "no-store",
    credentials: "omit",
    signal,
  });
  if (!response.ok) {
    throw new Error(`geocode_${response.status}`);
  }

  const payload: unknown = await response.json();
  if (!isRecord(payload) || !Array.isArray(payload.results)) {
    throw new Error("invalid_geocode_response");
  }
  if (payload.results.length > MAX_RESULTS) {
    throw new Error("invalid_geocode_response");
  }

  return {
    results: payload.results.map(readResult),
    provider: readProvider(payload.provider),
  };
}
